import { useDateNow, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  ContentLabel,
  ContentValue,
  DisplayFormattedDate
} from 'components/common';
import { Box } from 'components/common/box';
import {
  AnswersProgress,
  CourseProgressStatus,
  ReadingProgress,
  TaskPointsProgress,
  VideoProgress
} from 'components/common/tasks';

import type { CourseProgress as CourseProgressData } from 'shared-types';
import type { CourseInView } from 'components/courses/index/Courses';

interface Props {
  course: CourseInView;
  progressData: CourseProgressData;
}

const CourseProgress = ({ course, progressData }: Props) => {
  const { dateNow } = useDateNow();

  const { messages, theme } = useReduxState();

  const deadlineIsActive =
    course.deadline &&
    (!progressData ||
      !progressData.completion ||
      progressData.completion.status !== 'success');

  return (
    <div className="progress">
      <Box title={messages.commonProgress}>
        <div className="inner">
          {course.completionEnabled ? (
            <ContentLabel
              horizontal={true}
              label={messages.commonStatus}
              spacing={20}
            >
              <ContentValue>
                <CourseProgressStatus
                  course={course}
                  id={`course-page-progress-status-${course.id}`}
                  progressData={progressData}
                />
              </ContentValue>
            </ContentLabel>
          ) : null}

          <ContentLabel
            horizontal={true}
            label={messages.tasksReading}
            spacing={20}
          >
            <ContentValue>
              <ReadingProgress
                analyticsDisabledForAdmin={
                  progressData.analyticsDisabledForAdmin
                }
                discrepancyInCompletion={Boolean(
                  progressData.discrepancyInCompletion
                )}
                readingProgress={progressData.readingProgress}
                readingProgressMaxTime={progressData.readingProgressMaxTime}
              />
            </ContentValue>
          </ContentLabel>

          <ContentLabel
            horizontal={true}
            label={messages.tasksVideos}
            spacing={20}
          >
            <ContentValue>
              <VideoProgress
                analyticsDisabledForAdmin={
                  progressData.analyticsDisabledForAdmin
                }
                numberOfVideos={progressData.numberOfVideos}
                videoProgress={progressData.videoProgress}
                videosWatched={progressData.videoProgressVideosWatched}
              />
            </ContentValue>
          </ContentLabel>

          <ContentLabel
            horizontal={true}
            label={messages.tasksAnswers}
            spacing={20}
          >
            <ContentValue>
              <AnswersProgress
                numberOfAnswers={progressData.taskAnswers}
                numberOfTasks={progressData.numberOfTasks}
              />
            </ContentValue>
          </ContentLabel>

          <ContentLabel
            horizontal={true}
            label={messages.tasksTaskPoints}
            spacing={20}
          >
            <ContentValue>
              <TaskPointsProgress
                numberOfTasks={progressData.numberOfTasks}
                taskMaxPoints={progressData.taskMaxPoints}
                taskPoints={progressData.taskPoints}
                taskPointsProgress={progressData.taskPointsProgress}
              />
            </ContentValue>
          </ContentLabel>

          {course.deadline ? (
            <ContentLabel
              horizontal={true}
              label={messages.courseTargetSchedule}
              spacing={40}
              style={{
                color: deadlineIsActive
                  ? Date.parse(course.deadline) < dateNow
                    ? theme.dangerColor
                    : theme.warningStandaloneColor
                  : theme.textColorLighter
              }}
            >
              <ContentValue
                icon={['far', 'clock']}
                style={{
                  color: deadlineIsActive
                    ? Date.parse(course.deadline) < dateNow
                      ? theme.dangerColor
                      : theme.warningStandaloneColor
                    : theme.textColorLighter
                }}
              >
                <div className="deadline">
                  <DisplayFormattedDate date={course.deadline} />
                </div>
              </ContentValue>
            </ContentLabel>
          ) : null}
        </div>
      </Box>

      <style jsx>{`
        @media (min-width: ${breakpoints.small}px) {
          .inner {
            padding: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default CourseProgress;
